import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Divider,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  IconButton as MuiIconButton,
  ThemeProvider,
  Tooltip,
  useMediaQuery,
} from '@mui/material'

import { AISearchFilledIcon } from '~assets/icons'
import { Avatar, Button, Icon, IconButton, SearchBase, Typography } from '~components/internal'
import { internalLightTheme } from '~createInternalTheme'
import SignOutDialog from '~pages/AccountSettingsScreen/components/SignOutDialog'
import { type RootState } from '~redux/rootReducer'
import {
  type SelectedWorkspace,
  setSelectedEIOrganization,
  setSelectedWorkspace,
} from '~redux/slices/organizationSlice'
import { type AppDispatch, persistor } from '~redux/store'
import { lightTokens } from '~tokens/internal'
import { type EmailIdentity, type IdentityId, type UserId, type WorkspaceId } from '~types'
import { loadResourcePermittedOrgIdentities } from '~utils/permissionsUtils'

import ActiveCallsButton from './ActiveCalls'
import CommandCenter from './CommandCenter'
import './TopBar.scss'
import { resolveWorkspaceDestination, useWorkspaceRedirectResolver } from './workspaceRedirect'
import WorkspaceSwitcherDialog from './WorkspaceSwitcherDialog'

const WORKSPACE_SEARCH_THRESHOLD = 8
const WORKSPACE_SYNC_CHANNEL = 'workspace-sync'
const WORKSPACE_FLUSH_TIMEOUT_MS = 500

const TAB_ID = Math.random().toString(36).slice(2)

const isMacPlatform = (): boolean => {
  if (typeof navigator === 'undefined') return false
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    navigator.userAgent
  return /mac|iphone|ipad|ipod/i.test(platform)
}

const TopBar = (): React.ReactElement => {
  const isMac = isMacPlatform()
  const user = useSelector((state: RootState) => state?.auth.user)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch: AppDispatch = useDispatch()
  const [searchOpen, setSearchOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false)
  const [signOutOpen, setSignOutOpen] = useState(false)
  const isCompact = useMediaQuery('(max-width: 900px)')
  const emailIdentities = useSelector(
    (state: RootState) => state?.organization.emailIdentities ?? [],
  )
  const selectedWorkspace = useSelector((state: RootState) => state?.organization.selectedWorkspace)
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const resolveWorkspaceRedirect = useWorkspaceRedirectResolver()

  const workspaces = useMemo<SelectedWorkspace[]>(() => {
    const byId = new Map<string, SelectedWorkspace>()
    for (const ei of emailIdentities) {
      if (ei.workspace_id && !byId.has(ei.workspace_id)) {
        byId.set(ei.workspace_id, {
          id: ei.workspace_id as WorkspaceId,
          name: ei.workspace_name,
          icon: ei.workspace_icon,
        })
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }),
    )
  }, [emailIdentities])

  const displayedWorkspace = selectedWorkspace ?? (workspaces.length === 1 ? workspaces[0] : null)
  const showSwitcher = workspaces.length > 1
  const isWorkspaceSearchable = workspaces.length > WORKSPACE_SEARCH_THRESHOLD

  const handleMenuClose = (): void => {
    setMenuAnchor(null)
  }

  const performWorkspaceSwitch = useCallback(
    (ws: SelectedWorkspace, targetOrg: EmailIdentity | null): void => {
      const destination = resolveWorkspaceDestination(
        location.pathname,
        location.search,
        targetOrg?.handle ?? null,
        resolveWorkspaceRedirect,
      )
      if (destination) flushSync(() => navigate(destination))
      dispatch(setSelectedWorkspace(ws))
      if (targetOrg) dispatch(setSelectedEIOrganization(targetOrg))
      void Promise.race([
        persistor.flush(),
        new Promise((resolve) => setTimeout(resolve, WORKSPACE_FLUSH_TIMEOUT_MS)),
      ]).finally(() => window.location.reload())
    },
    [dispatch, location.pathname, location.search, navigate, resolveWorkspaceRedirect],
  )

  const handleSelectWorkspace = async (ws: SelectedWorkspace): Promise<void> => {
    const orgsInWorkspace = emailIdentities.filter((ei) => ei.workspace_id === ws.id)
    const rootOrgId =
      orgsInWorkspace.find((ei) => ei.workspace_root_org_id)?.workspace_root_org_id ?? null
    const rootOrg = rootOrgId ? (orgsInWorkspace.find((ei) => ei.id === rootOrgId) ?? null) : null

    let casesReadOrgIds = new Set<string>()
    if (userId) {
      const perms = await loadResourcePermittedOrgIdentities(userId as UserId, 'cases').catch(
        () => null,
      )
      if (perms) casesReadOrgIds = new Set(perms.read)
    }
    const canUse = (ei: (typeof orgsInWorkspace)[number]): boolean =>
      casesReadOrgIds.size === 0 || casesReadOrgIds.has(ei.id)
    const targetOrg =
      (rootOrg && canUse(rootOrg) ? rootOrg : undefined) ??
      orgsInWorkspace.find(canUse) ??
      rootOrg ??
      orgsInWorkspace[0] ??
      null

    setWorkspaceDialogOpen(false)
    handleMenuClose()

    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(WORKSPACE_SYNC_CHANNEL)
      channel.postMessage({
        type: 'workspace-changed',
        workspaceId: ws.id,
        senderId: TAB_ID,
        targetOrgHandle: targetOrg?.handle ?? null,
      })
      channel.close()
    }

    performWorkspaceSwitch(ws, targetOrg)
  }

  const toggleCommandCenter = useCallback((): void => {
    setSearchOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        toggleCommandCenter()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return (): void => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleCommandCenter])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined
    const channel = new BroadcastChannel(WORKSPACE_SYNC_CHANNEL)
    let onVisible: (() => void) | undefined
    channel.onmessage = (event: MessageEvent): void => {
      if (event.data?.type !== 'workspace-changed') return
      if (event.data?.senderId === TAB_ID) return
      const workspaceId = event.data?.workspaceId as WorkspaceId
      const targetOrgHandle: string | null = event.data?.targetOrgHandle ?? null
      const ws = workspaces.find((w) => w.id === workspaceId) ?? {
        id: workspaceId,
        name: null,
        icon: null,
      }
      const targetOrg = targetOrgHandle
        ? (emailIdentities.find((ei) => ei.handle === targetOrgHandle) ?? null)
        : null
      const apply = (): void => performWorkspaceSwitch(ws, targetOrg)
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        apply()
        return
      }
      onVisible = (): void => {
        if (document.visibilityState === 'visible') {
          document.removeEventListener('visibilitychange', onVisible as () => void)
          apply()
        }
      }
      document.addEventListener('visibilitychange', onVisible)
    }
    return (): void => {
      channel.close()
      if (onVisible) document.removeEventListener('visibilitychange', onVisible)
    }
  }, [emailIdentities, performWorkspaceSwitch, workspaces])

  return (
    <ThemeProvider theme={internalLightTheme}>
      <Box className='top-bar'>
        <Box className='top-bar__workspace'>
          {displayedWorkspace && (
            <>
              <Avatar
                src={displayedWorkspace.icon ?? undefined}
                alt={displayedWorkspace.name ?? 'Workspace'}
                size='sm'
              />
              <Typography variant='subtitle2' noWrap className='top-bar__workspace-name'>
                {displayedWorkspace.name}
              </Typography>
            </>
          )}
        </Box>
        <Box className='top-bar__search-wrap'>
          <SearchBase
            readOnly
            onClick={() => setSearchOpen(true)}
            placeholder='Search, ask a question, or quick create'
            className='top-bar__search'
            startAdornment={
              <Box className='top-bar__search-icon'>
                <Icon name='search' fontSize='small' />
              </Box>
            }
            endAdornment={
              <InputAdornment position='end'>
                <Box className='top-bar__shortcut' aria-label={isMac ? 'Command K' : 'Control K'}>
                  {isMac ? <Icon name='KeyboardCommandKey' size={12} /> : <span>Ctrl</span>}
                  <span>K</span>
                </Box>
              </InputAdornment>
            }
          />
        </Box>
        <CommandCenter open={searchOpen} onClose={() => setSearchOpen(false)} />
        <Box className='top-bar__actions'>
          <ActiveCallsButton />
          {!location.pathname.startsWith('/assistant') &&
            (isCompact ? (
              <Tooltip title='Assistant'>
                <IconButton aria-label='Assistant' onClick={() => navigate('/assistant')}>
                  <AISearchFilledIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                startIcon={<AISearchFilledIcon />}
                variant='outlined'
                size='small'
                onClick={() => {
                  navigate('/assistant')
                }}
              >
                Assistant
              </Button>
            ))}
          {user && (
            <MuiIconButton
              aria-label='Account menu'
              aria-haspopup='true'
              aria-expanded={Boolean(menuAnchor)}
              sx={menuAnchor ? { backgroundColor: 'action.hover' } : undefined}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <Avatar
                src={user.image ?? undefined}
                identityId={user.identity_id as IdentityId}
                size='md'
              />
            </MuiIconButton>
          )}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              list: {
                autoFocusItem: Boolean(menuAnchor),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Tab') e.stopPropagation()
                },
                sx: {
                  '& .MuiMenuItem-root.Mui-focusVisible': {
                    backgroundColor: 'action.hover',
                  },
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose()
                navigate('/account')
              }}
            >
              <ListItemIcon>
                <Icon name='person' fontSize='small' />
              </ListItemIcon>
              <ListItemText>
                <Typography variant='body2'>Account Settings</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose()
                window.open(
                  'https://polimorphic.notion.site/Help-Support-361e2563f49f46f98bc20d46074d3d2d',
                  '_blank',
                )
              }}
            >
              <ListItemIcon>
                <Icon fill={0} name='info' fontSize='small' />
              </ListItemIcon>
              <ListItemText>
                <Typography variant='body2'>Help &amp; Support</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose()
                navigate('/changelog')
              }}
            >
              <ListItemIcon>
                <Icon name='history' fontSize='small' />
              </ListItemIcon>
              <ListItemText>
                <Typography variant='body2'>Change Log</Typography>
              </ListItemText>
            </MenuItem>
            <Divider />
            {showSwitcher && (
              <MenuItem
                onClick={() => {
                  handleMenuClose()
                  setWorkspaceDialogOpen(true)
                }}
              >
                <ListItemIcon>
                  <Icon name='apartment' fontSize='small' />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant='body2'>Change workspace</Typography>
                </ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose()
                setSignOutOpen(true)
              }}
            >
              <ListItemIcon>
                <Icon name='logout' fontSize='small' color={lightTokens.error.main} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant='body2' color='error'>
                  Sign out
                </Typography>
              </ListItemText>
            </MenuItem>
          </Menu>
          <WorkspaceSwitcherDialog
            open={workspaceDialogOpen}
            onClose={() => setWorkspaceDialogOpen(false)}
            workspaces={workspaces}
            currentWorkspaceId={displayedWorkspace?.id}
            searchable={isWorkspaceSearchable}
            onSelect={handleSelectWorkspace}
          />
        </Box>
      </Box>
      <SignOutDialog open={signOutOpen} onClose={() => setSignOutOpen(false)} />
    </ThemeProvider>
  )
}

export default TopBar
